import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { failureError, invalidError } from '@/components/pbo/notice'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { withRequired } from '@/utils/requiredCells'
import { comboQueryKeys, useGetComboList, type ComboParams, type ComboRow } from './queries/useGetComboList'
import { useDeleteCombos, useSaveCombos } from './queries/useComboMutations'
import { invalidReason } from './validate'
import { BE_REQUIRED_KEYS, GRID_CDS, MENU_CODE, MESSAGES, SAVE_REQUIRED_CANDIDATES, TABLE_ID } from './consts'
import ComboManagementView, { type ActionCtx, type Row, type Wiring } from './ComboManagementView'

/**
 * 콤보관리(SY12) — 1.0 SY05 를 2.0 으로 옮긴 화면(LOGISPRD-16045, BE LOGISPRD-17281). 1.0 SY05 와 병행 운영하려고 새 메뉴코드 SY12 를 쓴다.
 *
 * 메뉴관리(sy11)·공통코드(sy10) 화면과 같은 배선 규약이다 — [추가]는 빈 행을 맨 위에, [저장]은 새 행 POST·기존 행 PUT, [삭제]는 DELETE 를 행별 순차로 보낸다.
 * 미저장 새 행 삭제는 화면에서만 지운다. 1.0 SY05 와 같게 기존 행도 콤보코드·옵션코드를 고칠 수 있고, 같은 코드 중복을 막지 않는다.
 * 버튼 권한·화면관리 그리드 설정(SY12_HEAD)은 1.0 SY05 처럼 레벨·설정을 따른다.
 */
/** 표 행 — key = BE 행 id(문자열). _id 없음 = [추가]로 생긴 미저장 행. __new = DS 표 계약(필수 미입력 표기). */
type EditableRow = Omit<ComboRow, '_id'> & { _id?: number } & Row & { key: string; __new?: boolean }
type SavedRow = EditableRow & { _id: number }

const toEditable = (row: ComboRow): EditableRow => ({ ...row, key: String(row._id) })

const toParams = (filters: Record<string, unknown>): ComboParams => {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  return { comboCode: text(filters.COMBO_CD), comboName: text(filters.COMBO_NM) }
}

/** BE 문구(apiErrorText) 우선, 빈 body 응답은 HTTP 상태를 함께 노출 — 공통코드 화면 규약. */
const apiErrMessage = (e: unknown) => {
  const status = (e as { response?: { status?: number } })?.response?.status
  return apiErrorText(e, MESSAGES.apiFailed(status))
}

/** 순차 저장·삭제 훅이 실패 오류에 싣는 앞선 반영 건수 */
const doneOf = (e: unknown) => (e as { done?: number })?.done ?? 0
const rowKeyOf = (r: Row) => String((r.key as string | number | undefined) ?? '')
const sameParams = (a: ComboParams, b: ComboParams) => JSON.stringify(a) === JSON.stringify(b)
const NO_ROWS: ComboRow[] = []

function ComboManagement() {
  const [params, setParams] = useState<ComboParams>({})
  const { data: fetchedRows = NO_ROWS, isFetching } = useGetComboList(params)
  const saveCombos = useSaveCombos()
  const deleteCombos = useDeleteCombos()
  const queryClient = useQueryClient()

  // 화면관리 그리드 설정 — 표 별표(뷰 permCols)와 [저장] 필수 검사가 같은 forTable(TABLE_ID).columns 를 지난다.
  //  BE 필수 칸(BE_REQUIRED_KEYS)은 설정이 필수를 꺼도 별표·검사에 남긴다(공통코드와 같다).
  const permissionSlot = usePermissionSlot(MENU_CODE, GRID_CDS)
  const permission = useMemo(() => withRequired(permissionSlot, { [TABLE_ID]: BE_REQUIRED_KEYS }), [permissionSlot])

  const [rows, setRows] = useState<EditableRow[]>([])
  const newSeqRef = useRef(0)
  useEffect(() => {
    // 저장 뒤 재조회가 [추가]한 미저장 행을 지우지 않게 앞에 남긴다. [조회]·[초기화]는 그 전에 걷는다.
    setRows((prev) => [...prev.filter((r) => r._id == null), ...fetchedRows.map(toEditable)])
  }, [fetchedRows])

  /** 저장 대상 — 선택 행을 편집 반영본에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedRows = (ctx: ActionCtx): EditableRow[] => {
    const picked = new Set(ctx.selected.map(rowKeyOf))
    return rows.filter((r) => picked.has(r.key))
  }

  /** [조회]·[초기화] — 같은 조건이면 캐시라 요청이 안 나가 그때만 무효화하고 편집을 버린다(1.0). 미저장 새 행은 걷는다. */
  const search = async (filters: Record<string, unknown>) => {
    const next = toParams(filters)
    const same = sameParams(next, params)
    setRows(same ? fetchedRows.map(toEditable) : [])
    setParams(next)
    if (same) await queryClient.invalidateQueries({ queryKey: comboQueryKeys.list(next) })
  }

  const wiring: Wiring = {
    permission,
    columnLayout: { [TABLE_ID]: coreTableLayoutSlot(GRID_CDS[TABLE_ID]) },
    rows: { ROWS: () => ({ rows, loading: isFetching }) },
    actions: {
      search,
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 행 키로 찾아 바뀐 것만 갈아 끼운다. 기존 행의 코드도 바꿀 수 있다(PUT 이 4칸 모두 받는다). */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        setRows((prev) => prev.map((r) => ({ ...r, ...(byKey.get(r.key) ?? {}) }) as EditableRow))
      },
      sy12AddButton: () => {
        newSeqRef.current += 1
        setRows((prev) => [
          { COMBO_CD: '', COMBO_NM: '', COMBO_OPT_CD: '', COMBO_OPT_NM: '', INS_DATE: '', INS_BY: '', UPD_DATE: '', UPD_BY: '', key: `new-${newSeqRef.current}`, __new: true },
          ...prev,
        ])
      },
      /** [저장] 확인 — 검증 실패·API 실패 모두 throw(확인창·선택 유지). 실패 지점에서 멈추므로 앞서 등록된 새 행은 미저장 목록에서 뺀다(다시 저장하면 같은 행이 한 번 더 생긴다 — 중복을 BE 가 막지 않는다). */
      sy12SaveConfirm_ok: async (ctx) => {
        const targets = selectedRows(ctx)
        if (targets.length === 0) throw invalidError(MESSAGES.noSelection)
        const reason = invalidReason(targets, permission.forTable(TABLE_ID).columns(SAVE_REQUIRED_CANDIDATES))
        if (reason) throw invalidError(reason)
        const dropPosted = (done: number) => {
          const posted = new Set(
            targets
              .slice(0, done)
              .filter((r) => r._id == null)
              .map((r) => r.key),
          )
          setRows((prev) => prev.filter((r) => !posted.has(r.key)))
        }
        try {
          await saveCombos.mutateAsync(targets)
          dropPosted(targets.length)
          // 저장 후 조회조건 유지 — 서버 반영값(새 행 id·수정자·수정일자)만 다시 읽는다.
          await queryClient.invalidateQueries({ queryKey: comboQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          dropPosted(done)
          throw failureError(done > 0 ? MESSAGES.partialFailed(targets.length, done, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
      /** [삭제] 확인 — 미저장 새 행은 화면에서만 지우고, 저장된 행은 행별 DELETE 한다. */
      sy12DeleteConfirm_ok: async (ctx) => {
        const targets = selectedRows(ctx)
        const local = new Set(targets.filter((r) => r._id == null).map((r) => r.key))
        setRows((prev) => prev.filter((r) => !local.has(r.key)))
        const ids = targets.filter((r): r is SavedRow => r._id != null).map((r) => r._id)
        if (ids.length === 0) return
        try {
          await deleteCombos.mutateAsync(ids)
          await queryClient.invalidateQueries({ queryKey: comboQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          const deleted = new Set(ids.slice(0, done))
          setRows((prev) => prev.filter((r) => r._id == null || !deleted.has(r._id)))
          // 확인창의 건수(선택 행 전체)와 맞춘다 — 화면에서만 지운 미저장 행도 반영 건수에 넣는다
          const removed = local.size + done
          throw failureError(removed > 0 ? MESSAGES.partialFailed(targets.length, removed, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
    },
    useBusy: () => saveCombos.isPending || deleteCombos.isPending,
    onReset: () => {
      search({}).catch(() => undefined)
    },
  }

  return <ComboManagementView wiring={wiring} />
}

export default ComboManagement
